import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Sparkles, Wand2, Video, Code, GraduationCap, Search, Languages, Mic, Coins, Trophy, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import InstallPill from '../components/jixxeo/InstallPill';

const tools = [
  { icon: Sparkles, label: 'AI Chat', desc: 'Ask anything' },
  { icon: Wand2, label: 'Image Studio', desc: 'Generate art' },
  { icon: Video, label: 'Cinema', desc: 'AI video' },
  { icon: Code, label: 'Code', desc: 'Build with AI' },
  { icon: GraduationCap, label: 'Homework', desc: 'Learn faster' },
  { icon: Search, label: 'Research', desc: 'Deep answers' },
  { icon: Languages, label: 'Translate', desc: 'Any language' },
  { icon: Mic, label: 'Voice', desc: 'Talk to AI' },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="space-y-14">
      <section className="relative pt-6 sm:pt-12">
        <InstallPill />
        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
          Ask. Create.<br />
          <span className="gradient-text">Earn.</span>
        </h1>
        <p className="mt-5 text-slate-400 max-w-xl text-base sm:text-lg leading-relaxed">
          The all-in-one AI platform where every conversation, creation and share earns you real rewards.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {user ? (
            <Link to="/ai"><Button data-testid="cta-open-app" size="lg" className="gradient-brand text-white shadow-xl shadow-violet-500/30 hover:opacity-90">Open App</Button></Link>
          ) : (
            <>
              <Link to="/register"><Button data-testid="cta-get-started" size="lg" className="gradient-brand text-white shadow-xl shadow-violet-500/30 hover:opacity-90">Get started — free</Button></Link>
              <Link to="/ai"><Button data-testid="cta-try-guest" size="lg" variant="outline" className="border-white/20 hover:bg-white/5">Try as guest</Button></Link>
              <Link to="/subscriptions"><Button data-testid="cta-see-pricing" size="lg" variant="ghost" className="hover:bg-white/5">See pricing</Button></Link>
            </>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Everything you need</h2>
          <span className="text-xs text-slate-500 uppercase tracking-wider">8 tools</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {tools.map((t) => (
            <Card key={t.label} data-testid={`tool-card-${t.label.toLowerCase().replace(/\s+/g,'-')}`} className="glass p-4 hover:border-violet-500/50 transition-colors group cursor-pointer">
              <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <t.icon className="w-5 h-5 text-white" />
              </div>
              <div className="font-semibold">{t.label}</div>
              <div className="text-xs text-slate-400 mt-1">{t.desc}</div>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Coins, title: 'Earn JXP', desc: 'Get rewarded for every action — chats, creations, missions, referrals.', color: 'from-amber-500 to-yellow-400' },
          { icon: Trophy, title: 'Cash out real value', desc: 'Reach $5 in JXP progress and cash out via PayPal, gift cards or bank.', color: 'from-emerald-500 to-teal-400' },
          { icon: Users, title: 'Social creator hub', desc: 'Publish AI art, sell prompts on the marketplace, build followers.', color: 'from-fuchsia-500 to-violet-400' },
        ].map(f => (
          <Card key={f.title} className="glass p-6">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-3`}>
              <f.icon className="w-5 h-5 text-slate-950" />
            </div>
            <div className="font-display text-lg font-bold">{f.title}</div>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
          </Card>
        ))}
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Simple pricing</h2>
          <Link to="/subscriptions" data-testid="landing-see-all-plans" className="text-sm text-violet-400 hover:underline">See all plans →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { name: 'Free', price: '$0', note: 'Try everything', accent: 'from-slate-500 to-slate-400' },
            { name: 'Premium', price: '$9.99', note: 'Most popular', accent: 'from-violet-500 to-fuchsia-500', popular: true },
            { name: 'Elite', price: '$24.99', note: 'Power creators', accent: 'from-amber-400 to-yellow-500' },
          ].map(p => (
            <Card key={p.name} data-testid={`landing-plan-${p.name.toLowerCase()}`} className={`glass p-5 relative ${p.popular ? 'ring-2 ring-violet-500' : ''}`}>
              {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 gradient-brand rounded-full text-[10px] font-bold">POPULAR</div>}
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${p.accent}`} />
              <div className="font-display font-bold text-lg mt-3">{p.name}</div>
              <div className="font-display text-3xl font-black mt-1">{p.price}<span className="text-xs font-normal text-slate-400">/mo</span></div>
              <div className="text-xs text-slate-400 mt-2">{p.note}</div>
              <Link to="/subscriptions"><Button data-testid={`landing-choose-${p.name.toLowerCase()}`} size="sm" className="w-full mt-4 gradient-brand text-white">Choose {p.name}</Button></Link>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
